/**
 * Q2a — N3 · Competência 3 (Geometria) · task-model H3.18.K01
 *
 *   "Dados um triângulo e um eixo de simetria numa malha quadriculada, marcar os
 *    vértices do triângulo simétrico."
 *
 * ADAPTADO DA QUESTÃO 02 do material no GeoGebra, que pedia para arrastar os vértices
 * de um triângulo até ele ficar simétrico ao outro. A habilidade foi DESDOBRADA em dois
 * itens, porque ela cobre duas tarefas de natureza diferente:
 *
 *  - ESTE, N3: na malha, o aluno conta quadradinhos até o eixo e marca a imagem de cada
 *    vértice. É a compreensão do efeito da reflexão (H3.18);
 *  - o de construção, N4 (H3.20.K01): sem malha, com régua e compasso — a mesma
 *    reflexão, agora como procedimento geométrico.
 *
 * TRÊS ORIENTAÇÕES DE EIXO. Vertical e horizontal são contagem direta; o eixo diagonal
 * obriga a pensar na perpendicular, que já não corre ao longo das linhas da malha — é
 * aí que aparece o erro de refletir "como se o eixo fosse vertical".
 *
 * O TRIÂNGULO NUNCA TEM SIMETRIA QUE DISFARCE O ERRO. Se o reflexo coincidisse com uma
 * simples translação do triângulo, quem só arrastou a figura para o outro lado
 * acertaria sem saber refletir. O gerador descarta esses triângulos.
 */

import type { Rng } from '../../core/rng.ts';
import { adjetivo, conta, type Genero } from '../../core/pt.ts';
import { sampleWhere } from '../../core/sampling.ts';
import type { ConstructionSnapshot, GeoId, GeoObject } from '../../geom/construction.ts';
import { EMPTY_SNAPSHOT } from '../../geom/construction.ts';
import type { Vec2 } from '../../geom/vec.ts';
import type { Rect, Shape } from '../../render/scene.ts';
import { scene } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  SIM_DISTANCIA,
  SIM_EIXO_ERRADO,
  SIM_INCOMPLETO,
  SIM_PONTOS_A_MAIS,
  SIM_TRANSLACAO,
} from '../../schema/errors.ts';
import type { ConstructionTarget, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type Inline } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';
import {
  aneisDeRevisao,
  gradeQuadriculada,
  LEGENDA_ANEIS,
  mesmoConjunto,
  mesmoPonto,
  passoTxt,
  pontosDoAluno,
} from '../_comum/plano.ts';

/** Unidades de desenho por quadradinho. */
export const U = 40;
/** A malha vai de 0 a M nos dois sentidos. */
export const M = 12;

/**
 * O eixo, como reflexão com parâmetro inteiro:
 *  - vertical   x = k/2   : (x, y) → (k − x, y)
 *  - horizontal y = k/2   : (x, y) → (x, k − y)
 *  - diagonal   y = x + k : (x, y) → (y − k, x + k)
 *  - antidiagonal y = −x + k : (x, y) → (k − y, k − x)
 * Com k inteiro, a imagem de um ponto da malha é sempre um ponto da malha.
 */
export type Orientacao = 'vertical' | 'horizontal' | 'diagonal' | 'antidiagonal';

export interface Eixo {
  readonly o: Orientacao;
  readonly k: number;
}

export function refletir(e: Eixo, p: Vec2): Vec2 {
  switch (e.o) {
    case 'vertical':
      return { x: e.k - p.x, y: p.y };
    case 'horizontal':
      return { x: p.x, y: e.k - p.y };
    case 'diagonal':
      return { x: p.y - e.k, y: p.x + e.k };
    case 'antidiagonal':
      return { x: e.k - p.y, y: e.k - p.x };
  }
}

/** O parâmetro do eixo de orientação `o` que leva `p` em `q`, se houver. */
function parametroQueLeva(o: Orientacao, p: Vec2, q: Vec2): number | null {
  switch (o) {
    case 'vertical':
      return p.y === q.y ? p.x + q.x : null;
    case 'horizontal':
      return p.x === q.x ? p.y + q.y : null;
    case 'diagonal':
      return q.x - p.y === p.x - q.y ? q.y - p.x : null;
    case 'antidiagonal':
      return q.x + p.y === p.x + q.y ? q.x + p.y : null;
  }
}

/** Distância de um ponto ao eixo, com sinal (o lado). */
export function lado(e: Eixo, p: Vec2): number {
  switch (e.o) {
    case 'vertical':
      return 2 * p.x - e.k;
    case 'horizontal':
      return 2 * p.y - e.k;
    case 'diagonal':
      return p.y - p.x - e.k;
    case 'antidiagonal':
      return p.x + p.y - e.k;
  }
}

/** Os dois pontos estão na mesma perpendicular ao eixo? */
function mesmaPerpendicular(e: Eixo, p: Vec2, q: Vec2): boolean {
  switch (e.o) {
    case 'vertical':
      return p.y === q.y;
    case 'horizontal':
      return p.x === q.x;
    case 'diagonal':
      return p.x + p.y === q.x + q.y;
    case 'antidiagonal':
      return p.x - p.y === q.x - q.y;
  }
}

export interface Contexto {
  readonly texto: string;
}

export const CONTEXTOS: readonly Contexto[] = [
  {
    texto:
      'Para montar um azulejo decorado, uma artista desenhou na malha quadriculada o triângulo ABC. O desenho completo deve ser simétrico em relação à reta r: a outra metade é o reflexo do triângulo, como se r fosse um espelho.',
  },
  {
    texto:
      'Uma folha de papel quadriculado foi dobrada sobre a reta r, e um triângulo ABC foi recortado com a folha dobrada. Ao desdobrar a folha, aparece outro triângulo recortado, simétrico ao primeiro em relação à dobra.',
  },
  {
    texto:
      'O projeto de um jardim é simétrico em relação ao caminho representado pela reta r. Na planta, desenhada numa malha quadriculada, um canteiro triangular ABC já foi traçado de um dos lados do caminho.',
  },
];

export interface Params {
  readonly eixo: Eixo;
  readonly contexto: number;
  readonly vertices: readonly [Vec2, Vec2, Vec2];
  readonly imagens: readonly [Vec2, Vec2, Vec2];
}

export const NOMES = ['A', 'B', 'C'] as const;

const dentro = (p: Vec2): boolean => p.x >= 1 && p.x <= M - 1 && p.y >= 1 && p.y <= M - 1;

function sortearEixo(g: Rng): Eixo {
  const o = g.pick<Orientacao>(['vertical', 'vertical', 'horizontal', 'diagonal', 'antidiagonal']);
  if (o === 'vertical' || o === 'horizontal')
    return { o, k: 2 * g.int(5, 7) - (g.bool(0.3) ? 1 : 0) };
  if (o === 'diagonal') return { o, k: g.int(-2, 2) };
  return { o, k: g.int(10, 14) };
}

/** O reflexo coincide com uma translação do triângulo (em alguma correspondência)? */
function reflexoEhTranslacao(vs: readonly Vec2[], im: readonly Vec2[]): boolean {
  const [a, b, c] = vs as readonly [Vec2, Vec2, Vec2];
  const perms: readonly (readonly [number, number, number])[] = [
    [0, 1, 2],
    [0, 2, 1],
    [1, 0, 2],
    [1, 2, 0],
    [2, 0, 1],
    [2, 1, 0],
  ];
  return perms.some(([i, j, k]) => {
    const pi = im[i] as Vec2;
    const pj = im[j] as Vec2;
    const pk = im[k] as Vec2;
    const d = { x: pi.x - a.x, y: pi.y - a.y };
    return pj.x - b.x === d.x && pj.y - b.y === d.y && pk.x - c.x === d.x && pk.y - c.y === d.y;
  });
}

export function candidato(g: Rng): Omit<Params, 'contexto'> | null {
  const eixo = sortearEixo(g);
  const sinal = g.bool() ? 1 : -1;
  const vs: Vec2[] = [];
  for (let n = 0; n < 60 && vs.length < 3; n++) {
    const p = { x: g.int(1, M - 1), y: g.int(1, M - 1) };
    const l = lado(eixo, p) * sinal;
    // Do lado escolhido, a pelo menos um quadradinho do eixo, e sem repetir.
    if (l < 2) continue;
    if (!dentro(refletir(eixo, p))) continue;
    if (vs.some((q) => mesmoPonto(p, q))) continue;
    vs.push(p);
  }
  if (vs.length < 3) return null;
  const [a, b, c] = vs as [Vec2, Vec2, Vec2];
  // Triângulo de verdade: área mínima e nenhum lado curto demais.
  const area2 = Math.abs((b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y));
  if (area2 < 8) return null;
  const lados = [
    [a, b],
    [b, c],
    [c, a],
  ].map(([p, q]) => Math.hypot((q as Vec2).x - (p as Vec2).x, (q as Vec2).y - (p as Vec2).y));
  if (Math.min(...lados) < 2) return null;
  const im = vs.map((p) => refletir(eixo, p)) as [Vec2, Vec2, Vec2];
  if (reflexoEhTranslacao(vs, im)) return null;
  // Vértices a distâncias diferentes do eixo: a mesma contagem não serve para todos.
  const dists = new Set(vs.map((p) => Math.abs(lado(eixo, p))));
  if (dists.size < 2) return null;
  return { eixo, vertices: [a, b, c], imagens: im };
}

function drawParams(rng: Rng): Params {
  const contexto = rng.int(0, CONTEXTOS.length - 1);
  const geo = sampleWhere(rng.fork('geometria'), candidato, (c) => c !== null, {
    label: 'triangulo',
    maxTries: 200,
  }) as Omit<Params, 'contexto'>;
  return { contexto, ...geo };
}

// ---------------------------------------------------------------------------
// Cenário
// ---------------------------------------------------------------------------

export const quadro = (): Rect => ({
  x: -0.9 * U,
  y: -0.9 * U,
  w: (M + 1.8) * U,
  h: (M + 1.8) * U,
});

/** Os dois extremos do eixo dentro da malha. */
function extremosDoEixo(e: Eixo): [Vec2, Vec2] {
  const h = e.k / 2;
  switch (e.o) {
    case 'vertical':
      return [
        { x: h, y: -0.4 },
        { x: h, y: M + 0.4 },
      ];
    case 'horizontal':
      return [
        { x: -0.4, y: h },
        { x: M + 0.4, y: h },
      ];
    case 'diagonal': {
      // y = x + k, recortada ao quadrado [−0,4; M + 0,4]
      const lo = -0.4;
      const hi = M + 0.4;
      const x0 = Math.max(lo, lo - e.k);
      const x1 = Math.min(hi, hi - e.k);
      return [
        { x: x0, y: x0 + e.k },
        { x: x1, y: x1 + e.k },
      ];
    }
    case 'antidiagonal': {
      const lo = -0.4;
      const hi = M + 0.4;
      const x0 = Math.max(lo, e.k - hi);
      const x1 = Math.min(hi, e.k - lo);
      return [
        { x: x0, y: e.k - x0 },
        { x: x1, y: e.k - x1 },
      ];
    }
  }
}

export function cenario(p: Params): Shape[] {
  const tela = (v: Vec2): Vec2 => ({ x: v.x * U, y: v.y * U });
  const [e0, e1] = extremosDoEixo(p.eixo);
  return [
    gradeQuadriculada(0, M, 0, M, U),
    { t: 'seg', a: tela(e0), b: tela(e1), role: 'given' },
    { t: 'label', at: tela(e1), text: [txt('r')], anchor: 'ne' },
    { t: 'poly', pts: p.vertices.map(tela), closed: true, role: 'given', fill: 'given-fill' },
    ...p.vertices.map((v, i): Shape => ({
      t: 'point',
      p: tela(v),
      role: 'given',
      name: NOMES[i] as string,
    })),
  ];
}

// ---------------------------------------------------------------------------
// Correção
// ---------------------------------------------------------------------------

const ORIENTACOES: readonly Orientacao[] = ['vertical', 'horizontal', 'diagonal', 'antidiagonal'];

/** Os pontos marcados são o reflexo do triângulo em ALGUM eixo de outra orientação? */
export function refletiuEmOutroEixo(p: Params, ms: readonly Vec2[]): boolean {
  if (ms.length !== 3) return false;
  const A = p.vertices[0];
  return ORIENTACOES.some((o) =>
    ms.some((m) => {
      const k = parametroQueLeva(o, A, m);
      if (k === null) return false;
      const e: Eixo = { o, k };
      if (o === p.eixo.o && k === p.eixo.k) return false;
      return mesmoConjunto(
        ms,
        p.vertices.map((v) => refletir(e, v)),
      );
    }),
  );
}

/** Os pontos marcados são o triângulo apenas deslocado, sem ser refletido? */
export function transladou(p: Params, ms: readonly Vec2[]): boolean {
  if (ms.length !== 3) return false;
  const A = p.vertices[0];
  return ms.some((m) => {
    const d = { x: m.x - A.x, y: m.y - A.y };
    if (d.x === 0 && d.y === 0) return false;
    return mesmoConjunto(
      ms,
      p.vertices.map((v) => ({ x: v.x + d.x, y: v.y + d.y })),
    );
  });
}

/** Cada ponto marcado está na perpendicular de um vértice, do outro lado, mas nem todos à distância certa. */
export function errouDistancia(p: Params, ms: readonly Vec2[]): boolean {
  if (ms.length !== 3) return false;
  const usados = new Set<number>();
  for (const m of ms) {
    const i = p.vertices.findIndex(
      (v, k) =>
        !usados.has(k) && mesmaPerpendicular(p.eixo, v, m) && lado(p.eixo, v) * lado(p.eixo, m) < 0,
    );
    if (i < 0) return false;
    usados.add(i);
  }
  return !mesmoConjunto(ms, p.imagens);
}

function alvoDe(p: Params): ConstructionTarget {
  const doAluno = (objs: ReadonlyMap<GeoId, GeoObject>): Vec2[] => pontosDoAluno(objs, U);
  const acertou = (ms: readonly Vec2[], i: number): boolean =>
    ms.some((m) => mesmoPonto(m, p.imagens[i] as Vec2));

  return {
    describe: [
      txt('Marque os vértices A′, B′ e C′ do triângulo simétrico a ABC em relação à reta r.'),
    ],

    predicate(objs) {
      const ok = mesmoConjunto(doAluno(objs), p.imagens);
      return {
        name: 'reflexoNaMalha',
        ok,
        residual: ok ? 0 : 1,
        unit: 'count',
        tolerance: 0,
        message: [
          txt(
            ok
              ? 'Os três pontos são os vértices do triângulo simétrico.'
              : 'Os pontos marcados não são os vértices do triângulo simétrico.',
          ),
        ],
      };
    },

    diagnostics: [
      {
        error: SIM_PONTOS_A_MAIS,
        test: (objs) => {
          const ms = doAluno(objs);
          return ms.length > 3 && p.imagens.every((r) => ms.some((m) => mesmoPonto(m, r)));
        },
      },
      { error: SIM_INCOMPLETO, test: (objs) => doAluno(objs).length < 3 },
      { error: SIM_TRANSLACAO, test: (objs) => transladou(p, doAluno(objs)) },
      { error: SIM_EIXO_ERRADO, test: (objs) => refletiuEmOutroEixo(p, doAluno(objs)) },
      { error: SIM_DISTANCIA, test: (objs) => errouDistancia(p, doAluno(objs)) },
    ],

    robustnessTrials: 0,

    partialCredit: p.imagens.map((_r, i) => ({
      id: `vertice-${NOMES[i]}`,
      label: `Marcou ${NOMES[i]}′ no lugar certo`,
      weight: 1 / 3,
      test: (objs: ReadonlyMap<GeoId, GeoObject>) => acertou(doAluno(objs), i),
    })),

    /**
     * Sem coordenadas: a malha não é numerada, e o comentário fala a língua dela —
     * quantos quadradinhos, em que direção.
     */
    analyze(objs) {
      const ms = doAluno(objs);
      const out: Inline[] = [];
      const certos = p.imagens.filter((r) => ms.some((m) => mesmoPonto(m, r))).length;
      const sobras = ms.filter((m) => !p.imagens.some((r) => mesmoPonto(m, r))).length;
      if (ms.length === 0) out.push(txt('Você não marcou nenhum ponto.'));
      else {
        out.push(
          txt(
            `Você marcou ${conta(ms.length, 'ponto')}: ` +
              `${conta(certos, 'vertice').adj('certo')}` +
              (sobras > 0 ? ` e ${conta(sobras, 'ponto')} fora do lugar.` : '.'),
          ),
        );
      }
      if (transladou(p, ms)) {
        out.push(
          br,
          txt(
            'Os pontos formam o triângulo ABC apenas deslocado, sem espelhar: a figura refletida fica "virada" em relação à original.',
          ),
        );
      } else if (refletiuEmOutroEixo(p, ms)) {
        out.push(
          br,
          txt('Os pontos formam um reflexo do triângulo, mas em relação a outra reta, e não a r.'),
        );
      } else if (errouDistancia(p, ms)) {
        out.push(
          br,
          txt(
            'Cada ponto está na perpendicular ao eixo que passa por um vértice, mas nem todos à mesma distância do eixo que o vértice original.',
          ),
        );
      }
      p.vertices.forEach((v, i) => {
        if (acertou(ms, i)) return;
        const im = p.imagens[i] as Vec2;
        const nome = NOMES[i] as string;
        out.push(
          br,
          strong(`${nome}′: `),
          txt(
            `de ${nome} até ${nome}′, anda-se ${passoTxt(im.x - v.x, im.y - v.y)} — o eixo corta esse caminho ao meio, em ângulo reto.`,
          ),
        );
      });
      return out;
    },

    reviewShapes(objs) {
      const ms = doAluno(objs);
      const certos = ms.filter((m) => p.imagens.some((r) => mesmoPonto(m, r)));
      const errados = ms.filter((m) => !p.imagens.some((r) => mesmoPonto(m, r)));
      const faltando = p.imagens.filter((r) => !ms.some((m) => mesmoPonto(m, r)));
      return [
        {
          t: 'poly',
          pts: p.imagens.map((v) => ({ x: v.x * U, y: v.y * U })),
          closed: true,
          role: 'construction',
        },
        ...aneisDeRevisao(certos, errados, faltando, U),
      ];
    },

    reviewLegend: [
      ...LEGENDA_ANEIS,
      txt(' O contorno tracejado é o triângulo simétrico esperado.'),
    ],
  };
}

// ---------------------------------------------------------------------------
// Resolução
// ---------------------------------------------------------------------------

/**
 * Como o eixo está desenhado, concordando com QUEM é descrito.
 *
 * Era uma tabela de textos prontos, e o mesmo texto servia a "o eixo é …" e a
 * "a reta r é …". Na diagonal saía "A reta r é inclinado" na descrição acessível da
 * figura — a frase que o leitor de tela lê em voz alta. O predicado não tinha como
 * saber o gênero do sujeito; agora o recebe.
 */
export function eixoTxt(o: Orientacao, g: Genero): string {
  if (o === 'vertical' || o === 'horizontal') return o;
  return `${adjetivo('inclinado', g, 'sg')}, cortando os quadradinhos na diagonal`;
}

function resolucao(p: Params): Inline[] {
  const diagonal = p.eixo.o === 'diagonal' || p.eixo.o === 'antidiagonal';
  const partes: Inline[] = [
    strong('Resolução. '),
    txt(
      'Na reflexão em relação a uma reta, cada ponto e sua imagem ficam na mesma perpendicular ao eixo, ' +
        'à mesma distância dele, em lados opostos.',
    ),
    br,
    txt(
      diagonal
        ? 'Como o eixo corta os quadradinhos na diagonal, a perpendicular também é diagonal: cada passo em direção ao eixo é "um para o lado e um para cima (ou para baixo)".'
        : `Como o eixo é ${eixoTxt(p.eixo.o, 'm')}, basta contar os quadradinhos de cada vértice até o eixo, na direção ${p.eixo.o === 'vertical' ? 'horizontal' : 'vertical'}, e contar o mesmo tanto do outro lado.`,
    ),
  ];
  p.vertices.forEach((v, i) => {
    const im = p.imagens[i] as Vec2;
    const nome = NOMES[i] as string;
    partes.push(br, txt(`De ${nome} até ${nome}′: ${passoTxt(im.x - v.x, im.y - v.y)}.`));
  });
  partes.push(
    br,
    txt(
      'O triângulo refletido fica "virado": percorrido na ordem A′, B′, C′, ele gira no sentido oposto ao de ABC. Por isso não basta deslocá-lo para o outro lado.',
    ),
  );
  return partes;
}

// ---------------------------------------------------------------------------

export const simetriaNaMalha: ItemTemplate<Params, ConstructionSnapshot> = {
  id: templateId('h3-18-K01-simetria-malha'),
  version: 1,
  taskModel: taskModelCode('H3.18.K01'),
  skills: [skillCode('H3.18')],
  axis: 'I',
  level: 'N3',
  title: 'Triângulo simétrico na malha quadriculada',
  responseModel: '2PL',

  difficultySources: ['steps.2'],

  params: drawParams,
  solve: () => EMPTY_SNAPSHOT,
  buggy: [],

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // A perpendicular deixa de correr pelas linhas da malha.
    if (p.eixo.o === 'diagonal' || p.eixo.o === 'antidiagonal') fontes.push('steps.3plus');
    return fontes;
  },

  build(p) {
    const ctx = CONTEXTOS[p.contexto] as Contexto;
    const diagonal = p.eixo.o === 'diagonal' || p.eixo.o === 'antidiagonal';
    const desc =
      `Malha quadriculada de ${M} por ${M} quadradinhos, sem numeração. A reta r é ${eixoTxt(p.eixo.o, 'f')}. ` +
      'De um lado dela está o triângulo ABC, com os vértices nos cruzamentos da malha. ' +
      'É possível marcar pontos nos cruzamentos da malha.';
    const cena = scene(cenario(p), {
      a11y: { title: 'Triângulo ABC e reta r na malha quadriculada', desc },
      viewBox: quadro(),
    });

    return {
      stem: [txt(ctx.texto)],
      comando: [
        txt('Marque, na malha, os vértices '),
        strong('A′, B′ e C′'),
        txt(' do triângulo simétrico ao triângulo ABC em relação à reta r.'),
      ],
      stimuli: [
        {
          kind: 'static-figure',
          motion: 'none',
          audio: { need: 'none' },
          id: 'malha',
          scene: cena,
          alt: desc,
        },
      ],
      interaction: {
        kind: 'construct',
        toolbelt: ['select', 'point', 'delete', 'undo'],
        givens: EMPTY_SNAPSHOT,
        backdrop: cenario(p),
        viewBox: quadro(),
        perturbable: [],
        target: alvoDe(p),
        snapping: {
          pixelRadius: 18,
          lattice: { on: true, step: U },
          toPoints: true,
          toIntersections: false,
          toObjects: false,
        },
        maxActions: 40,
        labelCreatedPoints: false,
        // Marcado o terceiro vértice, os lados do triângulo aparecem.
        ligarPontos: 3,
      },
      rationale: resolucao(p),
      declared: diagonal ? 'dificil' : 'facil',
    };
  },

  validate(_built, p) {
    if (!p.imagens.every(dentro)) return 'o reflexo sai da malha';
    if (reflexoEhTranslacao(p.vertices, p.imagens)) return 'o reflexo coincide com uma translação';
    return null;
  },
};
